<script lang="ts">
  import { Button } from "$lib/components/ui/button"
  import { Input } from "$lib/components/ui/input"
  import { MoveRight } from "lucide-svelte"
  import userStore from "$lib/stores/userStore"

  interface Requirement {
    name: string
    value?: string | string[]
  }

  interface Setting {
    name: string
    value?: boolean
  }

  interface ActionData {
    selectedAction?: ActionData
    requirements: Requirement[]
    settings?: Setting[]
  }

  interface CompetitionData {
    [key: string]: unknown
  }

  interface UserAge {
    age: number
    format: string
  }

  interface ExtendedUserData {
    email: string
    name?: string
    firstName?: string
    lastName?: string
    dateOfBirth?: string | null
    entryId?: string
    completedActionIds?: string[]
    completedActions?: unknown[]
    userFirstName?: string | null
    userLastName?: string | null
    userPhoneNumber?: string | null
    userAge?: UserAge | null
  }

  interface Props {
    action: ActionData
    competition: CompetitionData
    processAction: (action: ActionData, verificationData?: unknown) => void
    isPreview?: boolean
  }

  let {
    action,
    competition,
    processAction,
    isPreview = false,
  }: Props = $props()

  let isSubmitting = $state(false)
  let verificationInput = $state("")
  let errorMessage = $state("")

  async function completeAction() {
    try {
      errorMessage = "" // Clear any previous errors

      let verificationData = null
      if (verificationInput) {
        verificationData = {
          verification: verificationInput,
        }
      }

      window.open(finalUrl, "_blank")

      processAction(action, verificationData)

      isSubmitting = true
    } catch (error) {
      console.error("Error verifying code:", error)
      errorMessage = "An unexpected error occurred. Please try again."
    } finally {
      isSubmitting = false
    }
  }

  let description = $derived(
    (action.selectedAction || action)?.requirements.find(
      (r: Requirement) => r.name === "Instructions",
    )?.value,
  )

  let verificationRequired = $derived(
    (action.selectedAction || action)?.settings?.find(
      (s: Setting) => s.name === "Verification Required",
    )?.value,
  )

  let baseUrl = $derived(
    (action.selectedAction || action)?.requirements.find(
      (s: Requirement) => s.name === "Url",
    )?.value,
  )

  let buttonText = $derived(
    (action.selectedAction || action)?.requirements.find(
      (s: Requirement) => s.name === "Button Text",
    )?.value,
  )

  // Get the selected custom user values from the action's options
  let selectedCustomValues = $derived(
    (action.selectedAction || action)?.requirements.find(
      (r: Requirement) =>
        r.name === "Pass custom user values to URL (optional)",
    )?.value || [],
  )

  // Create final URL with custom parameters
  let finalUrl = $derived.by(() => {
    if (!baseUrl || typeof baseUrl !== "string") return ""

    let finalUrl = baseUrl
    const user = $userStore.user as ExtendedUserData | null

    if (user && selectedCustomValues && Array.isArray(selectedCustomValues)) {
      selectedCustomValues.forEach((paramValue: string) => {
        switch (paramValue) {
          case "email": {
            if (user.email) {
              finalUrl = finalUrl.replace("[email]", encodeURIComponent(user.email))
            }
            break
          }
          case "full_name": {
            let fullName = user.name
            if (!fullName) {
              fullName = `${user.userFirstName} ${user.userLastName}`
            }
            if (fullName) {
              finalUrl = finalUrl.replace("[full_name]", encodeURIComponent(fullName))
            }
            break
          }
          case "first_name": {
            const firstName =
              user.userFirstName ||
              user.firstName ||
              user.name?.split(" ")[0] ||
              ""
            if (firstName) {
              finalUrl = finalUrl.replace("[first_name]", encodeURIComponent(firstName))
            }
            break
          }
          case "last_name": {
            const lastName =
              user.userLastName ||
              user.lastName ||
              user.name?.split(" ").slice(1).join(" ") ||
              ""
            if (lastName) {
              finalUrl = finalUrl.replace("[last_name]", encodeURIComponent(lastName))
              finalUrl = finalUrl.replace("[lname]", encodeURIComponent(lastName))
            }
            break
          }
          case "age": {
            if (user.userAge?.age) {
              finalUrl = finalUrl.replace("[age]", encodeURIComponent(user.userAge.age.toString()))
            }
            break
          }
          case "phone_number": {
            if (user.userPhoneNumber) {
              finalUrl = finalUrl.replace("[phone]", encodeURIComponent(user.userPhoneNumber))
            }
            break
          }
          case "country": {
            // Add country logic if available in user data
            // This might need to be implemented based on how country data is stored
            break
          }
        }
      })
    }

    console.dir(finalUrl, { maxStringLength: null })

    return finalUrl
  })
</script>

{#if isPreview}
  <div class="p-4">
    <div>
      {#if description}
        <div class="text-black description-content">
          {@html description}
        </div>
      {:else}
        <p class="text-base font-semibold">Instructions:</p>
        <ol class="list-disc list-inside mt-2 text-sm leading-6">
          <li>Provide instructions for how to complete the action</li>
          <li>
            Provide a link to the page where users can complete the action
          </li>
        </ol>
        <p class="text-sm leading-6 my-2">
          If the code is correct, your bonus entries will be added instantly!
        </p>
      {/if}
    </div>
    <div class="mt-2 flex items-center space-x-2 max-w-md">
      {#if verificationRequired}
        <Input
          type="text"
          bind:value={verificationInput}
          placeholder="Input verification data"
          class="flex-1"
          disabled
        />
        <Button disabled>Submit</Button>
      {:else}
        <Button class="w-full" disabled>
          {#if buttonText}
            {buttonText}
          {/if}
          <MoveRight />
        </Button>
      {/if}
    </div>
  </div>
{:else}
  <div class="p-4">
    <div class="space-y-4 max-w-[400px]">
      {#if description}
        <div class="text-[15px] text-black description-content">
          {@html description}
        </div>
      {:else}
        <p class="text-base font-semibold">Instructions:</p>
        <ol class="list-disc list-inside mt-2 text-sm leading-6">
          <li>Provide instructions for how to complete the action</li>
          <li>
            Provide a link to the page where users can complete the action
          </li>
        </ol>
        <p class="text-sm leading-6 my-2">
          If the code is correct, your bonus entries will be added instantly!
        </p>
      {/if}
    </div>

    <div class="flex flex-col space-y-2 max-w-md">
      {#if errorMessage}
        <p class="text-sm text-red-500">{errorMessage}</p>
      {/if}

      <div class="flex items-center space-x-2 mt-4">
        {#if verificationRequired}
          <Input
            type="text"
            bind:value={verificationInput}
            placeholder="Input verification data"
            class="w-4/5"
          />
        {/if}
        <Button
          disabled={isSubmitting ||
            (verificationRequired && !verificationInput)}
          onclick={completeAction}
          class="text-center {verificationRequired ? '' : 'w-full'}"
        >
          {#if isSubmitting}
            <span class="mr-2">
              <svg
                class="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </span>
          {:else}
            {#if buttonText}
              {buttonText}
            {/if}
            <MoveRight />
          {/if}
        </Button>
      </div>
    </div>
  </div>
{/if}
